/**
 * Step-transition rules for the C3 wizard — a 1:1 port of Android's `OrderFlowStateMachine`.
 *
 * Pure, with no React or router dependency, so the whole machine is testable without mounting
 * anything. {@link canAdvance} is the single source of truth for **both** the «Далее» button's
 * enabled state and the guard re-checked before actually moving: a disabled button is a hint, not
 * a control, and keyboard, autofill and a resumed draft all reach the transition without it.
 */
import { isWindowOfferable } from './deliveryWindow';
import { isAsapOpen } from './serviceHours';
import type { OrderDraft, OrderStep } from './draft';
import { validateLiters } from './liters';
import { hasPaymentChoice } from './paymentChoice';
import { extraCarsValid, minOrderStatus, validateTotalVolume } from './volume';

/** In order. The wizard is one screen whose sheet content changes, not six routes. */
export const ORDER_STEPS: readonly OrderStep[] = [
  'address',
  'fuel',
  'presence',
  'time',
  'payment',
  'summary',
];

/** `null` at `address` means «leave the wizard entirely» — the caller decides where to. */
export function previousStep(step: OrderStep): OrderStep | null {
  const index = ORDER_STEPS.indexOf(step);
  return index <= 0 ? null : (ORDER_STEPS[index - 1] ?? null);
}

/** `null` at `summary` means «no next step» — it is the terminal review screen. */
export function nextStep(step: OrderStep): OrderStep | null {
  const index = ORDER_STEPS.indexOf(step);
  if (index < 0 || index === ORDER_STEPS.length - 1) return null;
  return ORDER_STEPS[index + 1] ?? null;
}

/**
 * Whether the draft currently satisfies this step's gate.
 *
 * `now` matters to the time step alone: a persisted draft outlives the window it was built
 * around, so that gate has to be time-aware rather than merely «something was picked» (audit
 * 2026-08-18). Injectable so tests are deterministic.
 */
export function canAdvance(
  step: OrderStep,
  draft: OrderDraft,
  now: Date = new Date(),
): boolean {
  switch (step) {
    case 'address':
      return canAdvanceFromAddress(draft);
    case 'fuel':
      return canAdvanceFromFuel(draft);
    case 'presence':
      return canAdvanceFromPresence(draft);
    case 'time':
      return canAdvanceFromTime(draft, now);
    case 'payment':
      return hasPaymentChoice(draft);
    case 'summary':
      return false;
  }
}

/**
 * A pin must be placed **and** the last zone check for it must have come back available.
 *
 * `null` (pending or never checked) blocks exactly as `false` does. Treating an unfinished check
 * as permission is a recurring source of subtle bugs, and it is the one case where the safe
 * default and the convenient one differ.
 */
function canAdvanceFromAddress(draft: OrderDraft): boolean {
  return draft.latitude !== null && draft.longitude !== null && draft.serviceAreaAvailable === true;
}

/**
 * Every car of the выезд, not only the primary one.
 *
 * An extra car with a blank or over-capacity volume used to walk all the way to the summary and
 * be refused by the backend on the payment screen. Both city limits are evaluated **once, across
 * the whole выезд**, because that is the shape the backend holds them in.
 */
function canAdvanceFromFuel(draft: OrderDraft): boolean {
  const primaryValid =
    draft.fuelMode === 'fullTank' ||
    validateLiters(draft.liters, draft.tankVolumeLiters).kind === 'valid';
  if (!primaryValid) return false;

  if (!extraCarsValid(draft.extraCars)) return false;
  if (validateTotalVolume(draft).kind !== 'valid') return false;

  // The city's floor blocks only where the volume *can* be raised: the tanks together reach the
  // minimum but the person has not asked for it. A выезд physically unable to reach the minimum
  // passes — a 15-litre tank is not greed, and refusing it would lock the service away from small
  // cars for good. A calm line on this same step, well before payment, explains the surcharge.
  return minOrderStatus(draft).kind !== 'shortOfMinimum';
}

/**
 * «Без меня» additionally requires the operator comment and granted notifications.
 *
 * The notification gate is a hard one — a disabled button, not warning text. Somebody who is not
 * at the car has no other way to learn that the driver arrived and could not get in.
 */
function canAdvanceFromPresence(draft: OrderDraft): boolean {
  // No «unanswered» branch: the draft always holds one of the two, and the step shows it selected
  // — see `OrderDraft.presence` for the dead button that taught us the difference.
  if (draft.presence === 'withMe') return true;
  return draft.comment.trim().length > 0 && draft.notificationsGranted;
}

function canAdvanceFromTime(draft: OrderDraft, now: Date): boolean {
  // Neither unscheduled mode needs anything picked — «Срочно» differs from «Ближайшая» in what it
  // costs and where it lands in the queue, not in what the person must fill in. But both exist
  // only in the spot's working hours (owner decision 30.09.2026): outside them «Далее» on either
  // would lead to a refusal on the payment screen.
  if (draft.timeMode !== 'scheduled') return isAsapOpen(draft.serviceHours, now);
  const window = draft.selectedWindow;
  return window !== null && isWindowOfferable(window, now);
}
